import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {
  RQ_ADD_INVENTORY_LOT_API,
  RQ_DELETE_INVENTORY_API,
  RQ_DELETE_INVENTORY_LOT_API,
  RQ_GET_ALL_INVENTORY_API,
  RQ_GET_INVENTORY_BY_ID_API,
  RQ_GET_INVENTORY_BY_LOT_API,
  RQ_INVENTORY_BY_FILTER_API,
  RQ_REGISTER_INVENTORY_API,
  RQ_UPDATED_INVENTORY_LOT_API,
  RQ_UPDATE_INVENTORY_API,
} from './constants';
import {rqCreateRequest} from './rqHttpUtils';

export const RqGetAllInventory = token => {
  return useQuery({
    queryKey: ['rq_get_all_inventory'],
    queryFn: async () => {
      const response = await rqCreateRequest(
        RQ_GET_ALL_INVENTORY_API,
        null,
        token,
      );

      return response || [];
    },
    enabled: !!token,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const RqGetInventoryById = (id, token, options = {}) => {
  return useQuery({
    queryKey: ['rq_get_inventory', id],
    queryFn: async () => {
      return rqCreateRequest(
        RQ_GET_INVENTORY_BY_ID_API,
        null,
        token,
        {id},
      );
    },
    enabled: !!id && !!token,
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};

export const RqGetInventoryByLot = (
  lotNumber,
  token,
  options = {},
) => {
  return useQuery({
    queryKey: ['rq_get_inventory_lot', lotNumber],
    queryFn: async () => {
      return rqCreateRequest(
        RQ_GET_INVENTORY_BY_LOT_API,
        null,
        token,
        {lotNumber},
      );
    },
    enabled: !!lotNumber && !!token,
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};

export const RqGetInventoryByFilter = (
  filters,
  token,
  options = {},
) => {
  return useQuery({
    queryKey: ['rq_get_inventory_by_filter', filters],
    queryFn: async ({queryKey, signal}) => {
      const filtersData = queryKey[1];

      if (
        !filtersData ||
        Object.keys(filtersData).length === 0
      ) {
        return [];
      }

      return rqCreateRequest(
        RQ_INVENTORY_BY_FILTER_API,
        filtersData,
        token,
        null,
        signal,
      );
    },
    enabled:
      Object.keys(filters || {}).length > 0 && !!token,
    keepPreviousData: true,
    ...options,
  });
};

export const RqRegisterInventory = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_register_inventory'],
    mutationFn: async ({payload, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_REGISTER_INVENTORY_API,
        payload,
        token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_inventory'],
      });
    },
    ...options,
  });
};

export const RqAddInventoryLot = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_add_inventory_lot'],
    mutationFn: async ({id, payload, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_ADD_INVENTORY_LOT_API,
        payload,
        token,
        {id},
      );
    },
    onSuccess: (data, variables) => {
      const {id} = variables;

      queryClient.invalidateQueries({
        queryKey: ['rq_get_inventory', id],
      });

      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_inventory'],
      });
    },
    ...options,
  });
};

export const RqUpdateInventory = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rq_update_inventory_allfields'],
    mutationFn: async ({id, payload, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_UPDATE_INVENTORY_API,
        payload,
        token,
        {id},
      );
    },
    onSuccess: (data, variables) => {
      const {id} = variables;

      queryClient.invalidateQueries({
        queryKey: ['rq_get_inventory', id],
      });

      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_inventory'],
      });
    },
    ...options,
  });
};

export const RqUpdateInventoryLot = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['rq_update_inventory_lot_allfields'],
    mutationFn: async ({lotId, payload, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_UPDATED_INVENTORY_LOT_API,
        payload,
        token,
        {lotId},
      );
    },
    onSuccess: (data, variables) => {
      const {lotId} = variables;

      queryClient.invalidateQueries({
        queryKey: ['rq_get_inventory_lot', lotId],
      });

      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_inventory'],
      });
    },
    ...options,
  });
};

export const RqDeleteInventoryLot = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['rq_delete_inventory_lot'],
    mutationFn: async ({lotId, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_DELETE_INVENTORY_LOT_API,
        null,
        token,
        {lotId},
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_inventory'],
      });
    },
    ...options,
  });
};

export const RqDeleteInventory = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['rq_delete_inventory'],
    mutationFn: async ({id, token}) => {
      if (!token) {
        throw new Error('Auth token missing');
      }

      return rqCreateRequest(
        RQ_DELETE_INVENTORY_API,
        null,
        token,
        {id},
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['rq_get_all_inventory'],
      });
    },
    ...options,
  });
};
